/* The confirmation the table reconciliation check needs, where it belongs.
 *
 * Until step 11 this was a four-step wizard on its own route, standing between
 * a reviewer and every finding in the document. It asked for work before it had
 * given anyone a reason to care about the answer. Now it is one block inside the
 * one check that needs it: the reviewer meets it having already read the
 * findings from the checks that need nothing, chooses to open it, and gets a
 * specific named check in return.
 *
 * "Skip for now" is always there and never costs anything. A skipped check is
 * reported as a check that did not run — never as one that passed.
 *
 * The rules — what the dropdowns offer, what a confirmed selection is, which
 * state the check is in — are in ./reconcileModel.ts. */

import type { ReactNode } from 'react';

import { Button, Disclosure, Label } from '../primitives';
import TableView from './TableView';
import {
  NO_TABLE,
  applyCorrection,
  detailOptions,
  measuresOf,
  pickDetail,
  pickSummary,
  rateOptions,
  summaryOptions,
  tablesInPlay,
  type ReconcilePicks,
  type TableOption,
} from './reconcileModel';
import type { ExtractedTable } from '../../types';
import './InlineReconcile.css';

/* ==========================================================================
   The shell both asks share
   ========================================================================== */

interface AnswerBlockProps {
  /** The bold first line. The whole ask, in one sentence. */
  lead: string;
  /** Why it is being asked, and what answering it buys. */
  body: string;
  /** The numbered questions. */
  children: ReactNode;
  /** The "Correct a figure" disclosure, where there are figures to correct. */
  extra?: ReactNode;
  confirmLabel: string;
  onConfirm(): void;
  onSkip(): void;
  /** True while the check is running, so the block cannot be fired twice. */
  running?: boolean;
  confirmDisabled?: boolean;
  className?: string;
}

function AnswerBlock({
  lead,
  body,
  children,
  extra,
  confirmLabel,
  onConfirm,
  onSkip,
  running = false,
  confirmDisabled = false,
  className,
}: AnswerBlockProps) {
  return (
    <section className={['ir', className].filter(Boolean).join(' ')}>
      <p className="ir__lead">{lead}</p>
      <p className="ir__body">{body}</p>

      <ol className="ir__questions">{children}</ol>

      {extra}

      <div className="ir__actions">
        <Button
          variant="primary"
          marker
          onClick={onConfirm}
          disabled={running || confirmDisabled}
        >
          {running ? 'Checking…' : confirmLabel}
        </Button>
        {/* Always available, on every state of every ask. */}
        <Button variant="ghost" onClick={onSkip} disabled={running}>
          Skip for now
        </Button>
      </div>
    </section>
  );
}

/* ==========================================================================
   One question
   ========================================================================== */

interface QuestionProps {
  question: string;
  /** One select, or several where the question is asked per measure. */
  children: ReactNode;
}

function Question({ question, children }: QuestionProps) {
  return (
    <li className="ir__question">
      <p className="ir__ask">{question}</p>
      <div className="ir__fields">{children}</div>
    </li>
  );
}

interface TablePickerProps {
  /** The measure this dropdown is for, where there is more than one. */
  label: string | null;
  options: TableOption[];
  value: string | null;
  onChange(next: string | null): void;
  /** Adds a "do not reconcile this one" choice at the end. */
  noneLabel?: string;
  /** Distinguishes the labelled selects from each other. */
  id: string;
}

function TablePicker({
  label,
  options,
  value,
  onChange,
  noneLabel,
  id,
}: TablePickerProps) {
  const chosen = options.find((o) => o.value === value) ?? null;

  return (
    <p className="ir__field">
      {label ? (
        <label className="ir__field-label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <select
        id={id}
        className="ir__select"
        value={value ?? NO_TABLE}
        aria-label={label ? undefined : 'Table'}
        onChange={(event) =>
          onChange(event.target.value === NO_TABLE ? null : event.target.value)
        }
      >
        {noneLabel ? <option value={NO_TABLE}>{noneLabel}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {/* Why the engine put this one first. Provenance for a guess, never a
          reason to accept it. */}
      {chosen?.hint ? <span className="ir__why">{chosen.hint}</span> : null}
    </p>
  );
}

/* ==========================================================================
   The two answers the check needs
   ========================================================================== */

export interface InlineReconcileProps {
  tables: ExtractedTable[];
  picks: ReconcilePicks;
  onPicks(next: ReconcilePicks): void;
  onConfirm(): void;
  onSkip(): void;
  running?: boolean;
}

export default function InlineReconcile({
  tables,
  picks,
  onPicks,
  onConfirm,
  onSkip,
  running = false,
}: InlineReconcileProps) {
  const measures = measuresOf(tables, picks.summaryTableId);
  const inPlay = tablesInPlay(picks, tables);

  return (
    <AnswerBlock
      lead="This check needs two answers from you."
      body="PDF table extraction isn’t perfect, and a misread table would produce a wrong finding. Confirm two things and the arithmetic below becomes exact."
      confirmLabel="Confirm and check"
      onConfirm={onConfirm}
      onSkip={onSkip}
      running={running}
      confirmDisabled={picks.summaryTableId === null}
      extra={
        inPlay.length > 0 ? (
          <Disclosure
            className="ir__correct"
            summary="Correct a figure"
            panelClassName="ir__correct-body"
          >
            <p className="ir__note">
              The tables you have chosen, exactly as they were read out of the
              PDF. Any figure can be typed over, and a corrected figure is marked
              as corrected wherever the check uses it.
            </p>
            {inPlay.map((table) => (
              <TableView
                key={table.id}
                table={table}
                corrections={picks.corrections[table.id] ?? {}}
                onCorrect={(key, value) =>
                  onPicks(applyCorrection(picks, table.id, key, value))
                }
              />
            ))}
          </Disclosure>
        ) : null
      }
    >
      <Question question="Which table is the project savings summary?">
        <TablePicker
          id="ir-summary"
          label={null}
          options={summaryOptions(tables)}
          value={picks.summaryTableId}
          onChange={(next) => onPicks(pickSummary(picks, tables, next))}
        />
      </Question>

      <Question question="Which table gives the detail for this measure?">
        {measures.length === 0 ? (
          <p className="ir__none">
            No measure rows could be read from the table you chose. Picking a
            different summary table is usually the fix.
          </p>
        ) : (
          measures.map((measure) => (
            <TablePicker
              key={measure.row}
              id={`ir-detail-${measure.row}`}
              // The measure names itself where the summary lists several, so a
              // reviewer can see which row each answer belongs to.
              label={measures.length > 1 ? measure.label : null}
              options={detailOptions(tables)}
              value={picks.detailByRow[measure.row] ?? null}
              onChange={(next) => onPicks(pickDetail(picks, measure.row, next))}
              noneLabel="Don’t reconcile this measure"
            />
          ))
        )}
      </Question>
    </AnswerBlock>
  );
}

/* ==========================================================================
   The one further answer, inside the finding that needs it
   ========================================================================== */

export interface InlineRateAnswerProps {
  /** The finding's own prompt, so the ask reads as being about that finding. */
  prompt: string;
  tables: ExtractedTable[];
  rateTableId: string | null;
  onRateTable(next: string | null): void;
  onConfirm(): void;
  onSkip(): void;
  running?: boolean;
}

export function InlineRateAnswer({
  prompt,
  tables,
  rateTableId,
  onRateTable,
  onConfirm,
  onSkip,
  running = false,
}: InlineRateAnswerProps) {
  return (
    <AnswerBlock
      className="ir--inside"
      lead="This finding needs one more answer from you."
      body={`${prompt} Confirm which table gives it and the check can say which of the two figures the plan’s own cost agrees with.`}
      confirmLabel="Confirm and check"
      onConfirm={onConfirm}
      onSkip={onSkip}
      running={running}
      confirmDisabled={rateTableId === null}
    >
      <Question question="Which table gives the energy rate?">
        <TablePicker
          id="ir-rate"
          label={null}
          options={rateOptions(tables)}
          value={rateTableId}
          onChange={onRateTable}
        />
      </Question>
    </AnswerBlock>
  );
}

/**
 * The rate question after the reviewer has skipped it, inside the finding it
 * belonged to. One line, and the way back to it: a skipped question is a
 * question not answered, not a question withdrawn.
 */
export function AnswerSetAside({
  note,
  onAnswer,
}: {
  note: string;
  onAnswer(): void;
}) {
  return (
    <p className="ir__aside">
      <span>{note}</span>{' '}
      <button type="button" className="ir__aside-link" onClick={onAnswer}>
        Answer it now
      </button>
    </p>
  );
}

/* ==========================================================================
   A check that did not run, said plainly
   ========================================================================== */

export interface CheckNotRunProps {
  reason: string;
  /**
   * What the check needed and where it went looking for it. A reviewer told
   * only that something did not run cannot tell whether that is a fact about
   * their document or about this tool; this is how they find out.
   */
  lookedFor?: string;
  /** Absent where the check cannot be run at all. */
  onAnswer?(): void;
}

/**
 * What a set-aside or unavailable check shows in the centre pane. It is never
 * dressed as a finding and never as a check that passed: it says what did not
 * happen, and where the reviewer can still change that, it offers the way.
 */
export function CheckNotRun({ reason, lookedFor, onAnswer }: CheckNotRunProps) {
  return (
    <div className="ir__not-run">
      <Label tone="amber">Not checked</Label>
      <p className="ir__not-run-body">{reason}</p>
      {lookedFor ? (
        <Disclosure
          variant="inline"
          className="ir__looked-for"
          summary="What it looked for"
        >
          <p className="ir__looked-for-body">{lookedFor}</p>
        </Disclosure>
      ) : null}
      {onAnswer ? (
        <Button variant="secondary" marker onClick={onAnswer}>
          Answer the two questions
        </Button>
      ) : null}
    </div>
  );
}
